import React, {Component} from 'react';
import { observable, action } from 'mobx';
import { observer, inject } from 'mobx-react';
import { ColorRing } from 'react-loader-spinner';
import PageTitle from "../../../components/common/PageTitle";
import Chart from "../../../utils/chart";
import { toast } from 'react-toastify';
import {
  Button,
  Col, Row,
  Card, CardHeader, CardBody,
} from "shards-react";
import moment from "moment";

// fromWrite 값에 대한 한글 라벨 및 색상 매핑
const FROM_WRITE_CONFIG = {
  'cafe24':         { label: '카페24 (자동수집)', color: '#5a6af0' },
  '일반리뷰':        { label: '관리자 작성', color: '#10b981' },
  'file':           { label: '대량 등록(파일)', color: '#f59e0b' },
  'widget_writer':  { label: '위젯 작성', color: '#8b5cf6' },
  'alim_writer':    { label: '알림톡 작성', color: '#06b6d4' },
  'sns_instagram':  { label: 'SNS (인스타)', color: '#e44d8a' },
  'sns_naver':      { label: 'SNS (네이버)', color: '#03c75a' },
  'sns_youtube':    { label: 'SNS (유튜브)', color: '#ff0000' },
  'unknown':        { label: '미분류', color: '#adb5bd' },
};

const getFromWriteLabel = (key) => {
  if (FROM_WRITE_CONFIG[key]) return FROM_WRITE_CONFIG[key].label;
  if (key && key.startsWith('sns_')) return 'SNS (' + key.replace('sns_', '') + ')';
  return key || '미분류';
};

const CHART_COLORS = [
  '#5a6af0', '#10b981', '#f59e0b', '#e44d8a', '#03c75a',
  '#ff0000', '#8b5cf6', '#06b6d4', '#f97316', '#64748b',
];

const getFromWriteColor = (key, idx) => {
  if (FROM_WRITE_CONFIG[key]) return FROM_WRITE_CONFIG[key].color;
  return CHART_COLORS[idx % CHART_COLORS.length];
};

// 3개 그룹 정의
const REVIEW_GROUPS = {
  admin:    { label: '관리자', icon: 'admin_panel_settings', color: '#5a6af0', types: ['cafe24', 'file', '일반리뷰'] },
  customer: { label: '고객', icon: 'person', color: '#8b5cf6', types: ['widget_writer', 'alim_writer'] },
  etc:      { label: '기타', icon: 'more_horiz', color: '#adb5bd', types: [] }, // 나머지 전부
};

@inject(({ AOA }) => ({ getAdminMonitorReport: AOA.getAdminMonitorReport }))
@inject(({ teamUser }) => ({ isLogin: teamUser.isLogin, me: teamUser.me }))

@observer
class AdminMonitor extends Component {

  @observable isLoading = true;
  @observable report = null;

  constructor(props) {
    super(props);
    this.trendChartRef = React.createRef();
    this.adminChartRef = React.createRef();
    this.customerChartRef = React.createRef();
    this.etcChartRef = React.createRef();
    this.trendChartInstance = null;
    this.adminChartInstance = null;
    this.customerChartInstance = null;
    this.etcChartInstance = null;
  }

  componentDidMount() {
    this.init();
  }

  componentWillUnmount() {
    if (this.trendChartInstance) this.trendChartInstance.destroy();
    if (this.adminChartInstance) this.adminChartInstance.destroy();
    if (this.customerChartInstance) this.customerChartInstance.destroy();
    if (this.etcChartInstance) this.etcChartInstance.destroy();
  }

  @action init = async (forceRefresh = false) => {
    try {
      this.isLoading = true;
      let res = await this.props.getAdminMonitorReport(forceRefresh ? { forceRefresh: true } : undefined);
      if (res) {
        this.report = res;
      }
    } catch (e) {
      toast.error('데이터 조회 중 오류가 발생했습니다.');
      console.error(e);
    } finally {
      this.isLoading = false;
      setTimeout(() => {
        this.renderTrendChart();
        this.renderGroupChart('admin', this.adminChartRef, 'adminChartInstance');
        this.renderGroupChart('customer', this.customerChartRef, 'customerChartInstance');
        this.renderGroupChart('etc', this.etcChartRef, 'etcChartInstance');
      }, 100);
    }
  };

  formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    let n = Number(num);
    if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k';
    return n.toLocaleString();
  };

  // 최근 30일 날짜 라벨 (오늘 제외)
  buildDateLabels = () => {
    const labels = [];
    const yesterday = moment().subtract(1, 'days').startOf('day');
    for (let i = 29; i >= 0; i--) {
      labels.push(moment(yesterday).subtract(i, 'days').format('YYYY-MM-DD'));
    }
    return labels;
  };

  // 주문/리뷰 추이 데이터
  buildTrendData = () => {
    const r = this.report || {};
    const orderTrend = r.orderTrend || [];
    const reviewTrend = r.reviewTrend || [];
    const labels = this.buildDateLabels();

    const orderMap = {};
    orderTrend.forEach(d => { orderMap[d._id] = d.total || 0; });
    const reviewMap = {};
    reviewTrend.forEach(d => { reviewMap[d._id] = d.count || 0; });

    const orderData = labels.map(d => orderMap[d] || 0);
    const reviewData = labels.map(d => reviewMap[d] || 0);
    const displayLabels = labels.map(d => moment(d).format('MM/DD'));

    return { labels: displayLabels, orderData, reviewData };
  };

  // 그룹별 fromWrite 추이 데이터 빌드
  buildGroupTrendData = (groupKey) => {
    const r = this.report || {};
    const fromWriteTrend = r.fromWriteTrend || [];
    const fromWriteCounts = r.fromWriteCounts || [];
    const labels = this.buildDateLabels();
    const group = REVIEW_GROUPS[groupKey];

    // 해당 그룹에 속하는 타입 필터링
    let types;
    if (groupKey === 'etc') {
      const definedTypes = [...REVIEW_GROUPS.admin.types, ...REVIEW_GROUPS.customer.types];
      types = fromWriteCounts.map(d => d._id).filter(t => !definedTypes.includes(t));
    } else {
      types = group.types.filter(t => fromWriteCounts.some(d => d._id === t));
    }

    // 타입별-날짜별 Map
    const dataMap = {};
    types.forEach(t => { dataMap[t] = {}; });
    fromWriteTrend.forEach(d => {
      const type = d._id.fromWrite;
      const date = d._id.date;
      if (dataMap[type]) dataMap[type][date] = d.count || 0;
    });

    const displayLabels = labels.map(d => moment(d).format('MM/DD'));

    const datasets = types.map((type, idx) => {
      const color = getFromWriteColor(type, idx);
      return {
        label: getFromWriteLabel(type),
        fill: 'start',
        data: labels.map(d => dataMap[type][d] || 0),
        borderColor: color,
        backgroundColor: color.replace(')', ', 0.06)').replace('rgb', 'rgba').replace('#', ''),
        pointBackgroundColor: '#ffffff',
        pointHoverBackgroundColor: color,
        pointBorderColor: color,
        borderWidth: 2,
        pointRadius: 1,
        pointHoverRadius: 4,
      };
    });

    // backgroundColor가 hex면 transparent로 fallback
    datasets.forEach(ds => {
      if (ds.backgroundColor && ds.backgroundColor.includes('#')) {
        ds.backgroundColor = 'transparent';
      }
    });

    return { labels: displayLabels, datasets };
  };

  renderTrendChart = () => {
    if (!this.trendChartRef.current || !this.report) return;
    if (this.trendChartInstance) this.trendChartInstance.destroy();

    const { labels, orderData, reviewData } = this.buildTrendData();

    this.trendChartInstance = new Chart(this.trendChartRef.current, {
      type: 'LineWithLine',
      data: {
        labels,
        datasets: [
          {
            label: '주문',
            fill: 'start',
            data: orderData,
            backgroundColor: 'rgba(90, 106, 240, 0.08)',
            borderColor: 'rgba(90, 106, 240, 1)',
            pointBackgroundColor: '#ffffff',
            pointHoverBackgroundColor: 'rgba(90, 106, 240, 1)',
            pointBorderColor: 'rgba(90, 106, 240, 0.6)',
            borderWidth: 2,
            pointRadius: 2,
            pointHoverRadius: 5,
            yAxisID: 'y-order',
          },
          {
            label: '리뷰',
            fill: 'start',
            data: reviewData,
            backgroundColor: 'rgba(16, 185, 129, 0.08)',
            borderColor: 'rgba(16, 185, 129, 1)',
            pointBackgroundColor: '#ffffff',
            pointHoverBackgroundColor: 'rgba(16, 185, 129, 1)',
            pointBorderColor: 'rgba(16, 185, 129, 0.6)',
            borderWidth: 2,
            pointRadius: 2,
            pointHoverRadius: 5,
            yAxisID: 'y-review',
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { position: 'top', labels: { usePointStyle: true, padding: 20, fontSize: 13, fontColor: '#5a6169' } },
        elements: { line: { tension: 0.3 } },
        scales: {
          xAxes: [{ gridLines: { display: true, color: 'rgba(0,0,0,0.04)' }, ticks: { fontSize: 11, fontColor: '#aaa', maxRotation: 0, callback(t, i) { return i % 3 === 0 ? t : ''; } } }],
          yAxes: [
            { id: 'y-order', position: 'left', gridLines: { display: true, color: 'rgba(0,0,0,0.04)' }, ticks: { fontSize: 11, fontColor: 'rgba(90,106,240,0.8)', beginAtZero: true, callback(t) { return t > 999 ? (t/1000).toFixed(1)+'K' : t; } }, scaleLabel: { display: true, labelString: '주문', fontColor: 'rgba(90,106,240,0.8)', fontSize: 12 } },
            { id: 'y-review', position: 'right', gridLines: { display: false }, ticks: { fontSize: 11, fontColor: 'rgba(16,185,129,0.8)', beginAtZero: true, callback(t) { return t > 999 ? (t/1000).toFixed(1)+'K' : t; } }, scaleLabel: { display: true, labelString: '리뷰', fontColor: 'rgba(16,185,129,0.8)', fontSize: 12 } },
          ]
        },
        hover: { mode: 'index', intersect: false },
        tooltips: {
          mode: 'index', intersect: false,
          backgroundColor: 'rgba(255,255,255,0.95)', titleFontColor: '#333', bodyFontColor: '#666',
          borderColor: '#e9ecef', borderWidth: 1, cornerRadius: 8, xPadding: 12, yPadding: 10,
          callbacks: { label(ti, data) { return ' ' + data.datasets[ti.datasetIndex].label + ': ' + Number(ti.yLabel || 0).toLocaleString() + '건'; } }
        }
      }
    });
  };

  renderGroupChart = (groupKey, chartRef, instanceKey) => {
    if (!chartRef.current || !this.report) return;
    if (this[instanceKey]) this[instanceKey].destroy();

    const { labels, datasets } = this.buildGroupTrendData(groupKey);

    this[instanceKey] = new Chart(chartRef.current, {
      type: 'LineWithLine',
      data: { labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { position: 'top', labels: { usePointStyle: true, padding: 16, fontSize: 12, fontColor: '#5a6169' } },
        elements: { line: { tension: 0.3 } },
        scales: {
          xAxes: [{ gridLines: { display: true, color: 'rgba(0,0,0,0.04)' }, ticks: { fontSize: 11, fontColor: '#aaa', maxRotation: 0, callback(t, i) { return i % 3 === 0 ? t : ''; } } }],
          yAxes: [{ gridLines: { display: true, color: 'rgba(0,0,0,0.04)' }, ticks: { fontSize: 11, fontColor: '#888', beginAtZero: true, callback(t) { return t > 999 ? (t/1000).toFixed(1)+'K' : t; } } }]
        },
        hover: { mode: 'index', intersect: false },
        tooltips: {
          mode: 'index', intersect: false,
          backgroundColor: 'rgba(255,255,255,0.95)', titleFontColor: '#333', bodyFontColor: '#666',
          borderColor: '#e9ecef', borderWidth: 1, cornerRadius: 8, xPadding: 12, yPadding: 10,
          callbacks: { label(ti, data) { return ' ' + data.datasets[ti.datasetIndex].label + ': ' + Number(ti.yLabel || 0).toLocaleString() + '건'; } }
        }
      }
    });
  };

  render() {
    if (this.isLoading) {
      return (
        <div className="text-center" style={{paddingTop: '100px'}}>
          <ColorRing visible={true} height="60" width="60" colors={['#5a6169', '#5a6169', '#5a6169', '#5a6169', '#5a6169']}/>
          <p style={{marginTop: '10px', color: '#999'}}>데이터 로딩 중...</p>
        </div>
      );
    }

    const r = this.report || {};
    const store = r.storeSummary || {};
    const order = r.orderSummary || {};
    const review = r.reviewSummary || {};
    const fromWriteCounts = r.fromWriteCounts || [];

    return (
      <div style={{padding: '0px 16px 30px'}}>
        <PageTitle title="통합 모니터" subtitle="스토어 관리" className="mb-4"/>

        <div style={{marginBottom: '12px', textAlign: 'right'}}>
          <Button size="sm" theme="light" onClick={() => this.init(true)}>
            <span className="material-icons" style={{fontSize: '14px', verticalAlign: 'middle', marginRight: '4px'}}>refresh</span>
            새로고침
          </Button>
          <span style={{marginLeft: '12px', fontSize: '12px', color: '#999'}}>
            {(this.report && this.report.computedAt ? moment(this.report.computedAt) : moment()).format('YYYY-MM-DD HH:mm')} 기준
          </span>
        </div>

        {/* === 1라인: 스토어, 유료샵 === */}
        <Row className="mb-3">
          <Col lg="6" md="6" sm="12" className="mb-3">
            <Card small>
              <CardBody style={{padding: '20px'}}>
                <div style={{display: 'flex', alignItems: 'baseline', gap: '12px'}}>
                  <div>
                    <div style={{fontSize: '13px', color: '#868e96', marginBottom: '4px'}}>유료스토어</div>
                    <div style={{fontSize: '28px', fontWeight: '700', color: '#333'}}>{this.formatNumber(store.paidStore)}</div>
                  </div>
                  <div style={{display: 'flex', gap: '14px', marginLeft: 'auto', alignItems: 'center'}}>
                    <div style={{textAlign: 'center'}}>
                      <div style={{fontSize: '11px', color: '#868e96'}}>정기결제</div>
                      <div style={{fontSize: '18px', fontWeight: '700', color: '#5a6af0'}}>{this.formatNumber(store.regularStore)}</div>
                    </div>
                    <div style={{textAlign: 'center'}}>
                      <div style={{fontSize: '11px', color: '#868e96'}}>일시불</div>
                      <div style={{fontSize: '18px', fontWeight: '700', color: '#f59e0b'}}>{this.formatNumber(store.onepayStore)}</div>
                    </div>
                    <div style={{width: '1px', height: '30px', backgroundColor: '#e9ecef'}}/>
                    <div style={{textAlign: 'center'}}>
                      <div style={{fontSize: '11px', color: '#868e96'}}>신규(주)</div>
                      <div style={{fontSize: '18px', fontWeight: '700', color: '#8b5cf6'}}>+{this.formatNumber(store.newPaidStoreWeekly)}</div>
                    </div>
                    <div style={{textAlign: 'center'}}>
                      <div style={{fontSize: '11px', color: '#868e96'}}>신규(월)</div>
                      <div style={{fontSize: '18px', fontWeight: '700', color: '#06b6d4'}}>+{this.formatNumber(store.newPaidStoreMonthly)}</div>
                    </div>
                  </div>
                </div>
                <div style={{marginTop: '10px', fontSize: '13px'}}>
                  <span style={{color: '#10b981', fontWeight: '600'}}>총스토어 {this.formatNumber(store.totalStore)}</span>
                  <span style={{color: '#ccc', margin: '0 8px'}}>|</span>
                  <span style={{color: '#f59e0b', fontWeight: '600'}}>무료 {this.formatNumber(store.freeStore)}</span>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg="6" md="6" sm="12" className="mb-3">
            <Card small>
              <CardBody style={{padding: '20px'}}>
                <div style={{display: 'flex', alignItems: 'baseline', gap: '12px'}}>
                  <div>
                    <div style={{fontSize: '13px', color: '#868e96', marginBottom: '4px'}}>유료샵</div>
                    <div style={{fontSize: '28px', fontWeight: '700', color: '#333'}}>{this.formatNumber(store.paidShop)}</div>
                  </div>
                  <div style={{display: 'flex', gap: '14px', marginLeft: 'auto', alignItems: 'center'}}>
                    <div style={{textAlign: 'center'}}>
                      <div style={{fontSize: '11px', color: '#868e96'}}>정기결제</div>
                      <div style={{fontSize: '18px', fontWeight: '700', color: '#5a6af0'}}>{this.formatNumber(store.regularShop)}</div>
                    </div>
                    <div style={{textAlign: 'center'}}>
                      <div style={{fontSize: '11px', color: '#868e96'}}>일시불</div>
                      <div style={{fontSize: '18px', fontWeight: '700', color: '#f59e0b'}}>{this.formatNumber(store.onepayShop)}</div>
                    </div>
                    <div style={{width: '1px', height: '30px', backgroundColor: '#e9ecef'}}/>
                    <div style={{textAlign: 'center'}}>
                      <div style={{fontSize: '11px', color: '#868e96'}}>신규(주)</div>
                      <div style={{fontSize: '18px', fontWeight: '700', color: '#8b5cf6'}}>+{this.formatNumber(store.newPaidShopWeekly)}</div>
                    </div>
                    <div style={{textAlign: 'center'}}>
                      <div style={{fontSize: '11px', color: '#868e96'}}>신규(월)</div>
                      <div style={{fontSize: '18px', fontWeight: '700', color: '#06b6d4'}}>+{this.formatNumber(store.newPaidShopMonthly)}</div>
                    </div>
                  </div>
                </div>
                <div style={{marginTop: '10px', fontSize: '13px'}}>
                  <span style={{color: '#10b981', fontWeight: '600'}}>총샵 {this.formatNumber(store.totalShop)}</span>
                  <span style={{color: '#ccc', margin: '0 8px'}}>|</span>
                  <span style={{color: '#f59e0b', fontWeight: '600'}}>무료이용 {this.formatNumber(store.freeUseShop)}</span>
                  <span style={{color: '#ccc', margin: '0 8px'}}>|</span>
                  <span style={{color: '#adb5bd', fontWeight: '600'}}>기타 {this.formatNumber(store.etcShop)}</span>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* === 2라인: 총 주문, 총 리뷰, 타입별 카운트 (9개 균등) === */}
        <div style={{display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '24px'}}>
          {[
            { label: '총 주문', value: order.total, color: '#f59e0b' },
            { label: '총 리뷰', value: review.total, color: '#8b5cf6' },
            ...fromWriteCounts.map((item, idx) => ({
              label: getFromWriteLabel(item._id),
              value: item.count,
              color: getFromWriteColor(item._id, idx),
              pct: review.total > 0 ? ((item.count / review.total) * 100).toFixed(1) : 0,
            }))
          ].map((card, i) => (
            <div key={i} style={{flex: '1 1 0', minWidth: '120px'}}>
              <Card small style={{height: '100%'}}>
                <CardBody style={{padding: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'center'}}>
                  <div style={{fontSize: '12px', color: '#868e96', marginBottom: '4px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                    {card.label}
                  </div>
                  <div style={{fontSize: '22px', fontWeight: '700', color: '#333'}}>{this.formatNumber(card.value)}</div>
                  {card.pct !== undefined && (
                    <div style={{fontSize: '11px', color: '#adb5bd', marginTop: '4px'}}>{card.pct}%</div>
                  )}
                </CardBody>
              </Card>
            </div>
          ))}
        </div>

        {/* === 주문/리뷰 어제/주/월 통계 === */}
        <Row className="mb-4">
          <Col lg="4" md="12" className="mb-3">
            <Card small>
              <CardHeader style={{padding: '16px 20px', borderBottom: '1px solid #e9ecef'}}>
                <span style={{fontWeight: '600', fontSize: '15px'}}>
                  <span className="material-icons" style={{fontSize: '18px', verticalAlign: 'middle', marginRight: '6px', color: '#5a6af0'}}>shopping_cart</span>
                  주문 현황
                </span>
              </CardHeader>
              <CardBody style={{padding: '20px'}}>
                <table style={{width: '100%', borderCollapse: 'collapse'}}>
                  <thead>
                    <tr style={{borderBottom: '2px solid #e9ecef'}}>
                      <th style={{padding: '10px 12px', textAlign: 'left', fontSize: '13px', color: '#868e96'}}>기간</th>
                      <th style={{padding: '10px 12px', textAlign: 'right', fontSize: '13px', color: '#868e96'}}>주문 수</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{borderBottom: '1px solid #f0f0f0'}}>
                      <td style={{padding: '12px', fontSize: '14px'}}>어제</td>
                      <td style={{padding: '12px', textAlign: 'right', fontWeight: '600', fontSize: '16px', color: '#5a6af0'}}>{this.formatNumber(order.yesterday)}</td>
                    </tr>
                    <tr style={{borderBottom: '1px solid #f0f0f0'}}>
                      <td style={{padding: '12px', fontSize: '14px'}}>이번 주</td>
                      <td style={{padding: '12px', textAlign: 'right', fontWeight: '600', fontSize: '16px', color: '#5a6af0'}}>{this.formatNumber(order.weekly)}</td>
                    </tr>
                    <tr>
                      <td style={{padding: '12px', fontSize: '14px'}}>이번 달</td>
                      <td style={{padding: '12px', textAlign: 'right', fontWeight: '600', fontSize: '16px', color: '#5a6af0'}}>{this.formatNumber(order.monthly)}</td>
                    </tr>
                  </tbody>
                </table>
              </CardBody>
            </Card>
          </Col>

          <Col lg="8" md="12" className="mb-3">
            <Card small>
              <CardHeader style={{padding: '16px 20px', borderBottom: '1px solid #e9ecef'}}>
                <span style={{fontWeight: '600', fontSize: '15px'}}>
                  <span className="material-icons" style={{fontSize: '18px', verticalAlign: 'middle', marginRight: '6px', color: '#10b981'}}>rate_review</span>
                  리뷰 현황
                </span>
              </CardHeader>
              <CardBody style={{padding: '20px'}}>
                <table style={{width: '100%', borderCollapse: 'collapse'}}>
                  <thead>
                    <tr style={{borderBottom: '2px solid #e9ecef'}}>
                      <th style={{padding: '10px 12px', textAlign: 'left', fontSize: '13px', color: '#868e96'}}>기간</th>
                      <th style={{padding: '10px 12px', textAlign: 'right', fontSize: '13px', color: '#868e96'}}>리뷰 수</th>
                      <th style={{padding: '10px 12px', textAlign: 'right', fontSize: '13px', color: '#868e96'}}>타입별 내역</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr style={{borderBottom: '1px solid #f0f0f0'}}>
                      <td style={{padding: '12px', fontSize: '14px'}}>어제</td>
                      <td style={{padding: '12px', textAlign: 'right', fontWeight: '600', fontSize: '16px', color: '#10b981'}}>{this.formatNumber(review.yesterday)}</td>
                      <td style={{padding: '12px', textAlign: 'right', fontSize: '12px', color: '#666'}}>
                        {(review.yesterdayByType || []).map((t, i) => (
                          <span key={t._id} style={{marginLeft: i > 0 ? '8px' : 0, whiteSpace: 'nowrap'}}>
                            <span style={{display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: getFromWriteColor(t._id, i), marginRight: '3px', verticalAlign: 'middle'}}/>
                            {getFromWriteLabel(t._id)} <strong>{this.formatNumber(t.count)}</strong>
                          </span>
                        ))}
                      </td>
                    </tr>
                    <tr style={{borderBottom: '1px solid #f0f0f0'}}>
                      <td style={{padding: '12px', fontSize: '14px'}}>이번 주</td>
                      <td style={{padding: '12px', textAlign: 'right', fontWeight: '600', fontSize: '16px', color: '#10b981'}}>{this.formatNumber(review.weekly)}</td>
                      <td style={{padding: '12px', textAlign: 'right', fontSize: '12px', color: '#666'}}>
                        {(review.weeklyByType || []).map((t, i) => (
                          <span key={t._id} style={{marginLeft: i > 0 ? '8px' : 0, whiteSpace: 'nowrap'}}>
                            <span style={{display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: getFromWriteColor(t._id, i), marginRight: '3px', verticalAlign: 'middle'}}/>
                            {getFromWriteLabel(t._id)} <strong>{this.formatNumber(t.count)}</strong>
                          </span>
                        ))}
                      </td>
                    </tr>
                    <tr>
                      <td style={{padding: '12px', fontSize: '14px'}}>이번 달</td>
                      <td style={{padding: '12px', textAlign: 'right', fontWeight: '600', fontSize: '16px', color: '#10b981'}}>{this.formatNumber(review.monthly)}</td>
                      <td style={{padding: '12px', textAlign: 'right', fontSize: '12px', color: '#666'}}>
                        {(review.monthlyByType || []).map((t, i) => (
                          <span key={t._id} style={{marginLeft: i > 0 ? '8px' : 0, whiteSpace: 'nowrap'}}>
                            <span style={{display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: getFromWriteColor(t._id, i), marginRight: '3px', verticalAlign: 'middle'}}/>
                            {getFromWriteLabel(t._id)} <strong>{this.formatNumber(t.count)}</strong>
                          </span>
                        ))}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* === 주문/리뷰 추이 통합 차트 === */}
        <Row className="mb-4">
          <Col md="12" className="mb-3">
            <Card small>
              <CardHeader style={{padding: '16px 20px', borderBottom: '1px solid #e9ecef'}}>
                <span style={{fontWeight: '600', fontSize: '15px'}}>
                  <span className="material-icons" style={{fontSize: '18px', verticalAlign: 'middle', marginRight: '6px', color: '#5a6af0'}}>insights</span>
                  주문 / 리뷰 추이 (최근 30일)
                </span>
              </CardHeader>
              <CardBody style={{padding: '20px'}}>
                <div style={{position: 'relative', height: '350px'}}>
                  <canvas ref={this.trendChartRef} />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* === 리뷰 작성 타입별 추이 차트 (3개 그룹) === */}
        <Row>
          <Col lg="4" md="12" className="mb-3">
            <Card small>
              <CardHeader style={{padding: '14px 20px', borderBottom: '1px solid #e9ecef'}}>
                <span style={{fontWeight: '600', fontSize: '14px'}}>
                  <span className="material-icons" style={{fontSize: '17px', verticalAlign: 'middle', marginRight: '5px', color: REVIEW_GROUPS.admin.color}}>admin_panel_settings</span>
                  관리자 (최근 30일)
                </span>
                <div style={{fontSize: '11px', color: '#adb5bd', marginTop: '2px'}}>카페24 자동수집, 대량 등록, 관리자 작성</div>
              </CardHeader>
              <CardBody style={{padding: '16px'}}>
                <div style={{position: 'relative', height: '280px'}}>
                  <canvas ref={this.adminChartRef} />
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg="4" md="12" className="mb-3">
            <Card small>
              <CardHeader style={{padding: '14px 20px', borderBottom: '1px solid #e9ecef'}}>
                <span style={{fontWeight: '600', fontSize: '14px'}}>
                  <span className="material-icons" style={{fontSize: '17px', verticalAlign: 'middle', marginRight: '5px', color: REVIEW_GROUPS.customer.color}}>person</span>
                  고객 (최근 30일)
                </span>
                <div style={{fontSize: '11px', color: '#adb5bd', marginTop: '2px'}}>위젯 작성, 알림톡 작성</div>
              </CardHeader>
              <CardBody style={{padding: '16px'}}>
                <div style={{position: 'relative', height: '280px'}}>
                  <canvas ref={this.customerChartRef} />
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg="4" md="12" className="mb-3">
            <Card small>
              <CardHeader style={{padding: '14px 20px', borderBottom: '1px solid #e9ecef'}}>
                <span style={{fontWeight: '600', fontSize: '14px'}}>
                  <span className="material-icons" style={{fontSize: '17px', verticalAlign: 'middle', marginRight: '5px', color: REVIEW_GROUPS.etc.color}}>more_horiz</span>
                  기타 (최근 30일)
                </span>
                <div style={{fontSize: '11px', color: '#adb5bd', marginTop: '2px'}}>미분류, SNS 등</div>
              </CardHeader>
              <CardBody style={{padding: '16px'}}>
                <div style={{position: 'relative', height: '280px'}}>
                  <canvas ref={this.etcChartRef} />
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

      </div>
    );
  }
}

export default AdminMonitor;
